import { unsafeCSS, html } from "lit";
import { property } from "lit/decorators.js";
import { e } from "../../scoped-registry-mixin-2fqf-E5n.js";
import { ValidationController } from "../../utilities/controllers/validation.js";
import { FormControlMixin } from "../../utilities/mixins/form-control.js";
import { requiredValidator } from "../../utilities/validators.js";
import { LeafElement } from "../leaf-element.js";
import { LeafRadio } from "../radio/radio.component.js";
import { LeafValidationMessage } from "../validation-message/validation-message.component.js";
const styles = '/*------------------------------------*\\\n    #RESET\n\\*------------------------------------*/\n/**\n * Border-Box http:/paulirish.com/2012/box-sizing-border-box-ftw/\n */\n*,\n::slotted(*),\n*:before,\n*:after {\n  box-sizing: border-box;\n}\n\n:host *,\n:host *::before,\n:host *::after {\n  box-sizing: inherit;\n}\n\n[hidden] {\n  display: none !important;\n}\n\n:host {\n  display: block;\n}\n:host .leaf-c-radiogroup__wrapper {\n  margin-left: auto;\n  width: calc(100% - var(--leaf-spacing-4));\n}\n:host ::slotted([slot=note]) {\n  display: block;\n  margin-top: var(--leaf-spacing-8);\n}\n\n:host([variant=rows]) ::slotted(leaf-radio:not(:first-child)) {\n  margin-top: var(--leaf-spacing-16);\n}\n\n:host([variant=columns]) slot:not([name]) {\n  align-items: center;\n  display: flex;\n}\n:host([variant=columns]) ::slotted(*:not(:first-child)),\n:host([variant=columns]) ::slotted(*:not(:last-child)) {\n  margin-right: var(--leaf-spacing-16);\n}\n\n:host([invalid]) ::slotted(leaf-radio) {\n  position: relative;\n}\n:host([invalid]) ::slotted(leaf-radio)::before {\n  border: var(--leaf-border-width-default) solid var(--leaf-color-border-error);\n  content: "";\n  border-radius: 50%;\n  height: var(--leaf-spacing-24);\n  position: absolute;\n  width: var(--leaf-spacing-24);\n}\n\n:host(:focus-visible) {\n  outline: 0;\n  outline: var(--leaf-border-width-button-default) solid var(--leaf-color-border-active-outline);\n}\n\n:host([invalid]:focus-visible) {\n  outline: 0;\n  outline: var(--leaf-border-width-button-default) solid var(--leaf-color-border-error);\n}';
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
let createUniqueId = 0;
const _LeafRadiogroup = class _LeafRadiogroup extends e(FormControlMixin(LeafElement)) {
  constructor() {
    super(...arguments);
    this.internals = this.attachInternals();
    this.validation = new ValidationController(this, {
      validators: [requiredValidator],
      nativeTarget: () => this
    });
    this.variant = "rows";
  }
  get buttons() {
    return Array.from(this.querySelectorAll("leaf-radio")) || [];
  }
  get note() {
    return this.querySelector('[slot="note"]');
  }
  connectedCallback() {
    var _a;
    super.connectedCallback();
    this.internals.role = "radiogroup";
    (_a = this.buttons) == null ? void 0 : _a.forEach((radio) => radio.checked = radio.value === this.value);
    this.validation.validate(this.value ? this.value : void 0);
    this.addEventListener("focusout", this.onFocusout);
  }
  async firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);
    await this.updateComplete;
    this.id || (this.id = `leaf-radiogroup-${createUniqueId++}`);
    if (this.note) {
      this.note.id = `${this.id}-note`;
      this.setAttribute("aria-describedby", this.note.id);
    }
    const callback = (mutationList) => {
      var _a;
      const { target } = mutationList.find((m) => m.attributeName === "checked" && m.oldValue === null) || {};
      if (target instanceof LeafRadio && target.value) {
        (_a = this.buttons) == null ? void 0 : _a.forEach((radio) => radio.checked = radio.value === target.value);
        this.value = target.value;
        this.setValueForm();
        this.validation.validate(this.value ? this.value : void 0);
      }
    };
    const observer = new MutationObserver(callback);
    observer.observe(this, {
      attributeFilter: ["checked"],
      attributeOldValue: true,
      subtree: true
    });
  }
  async updated(changes) {
    await this.updateComplete;
    if (changes.has("invalid")) {
      this.buttons.forEach((radio) => radio.invalid = this.invalid);
    }
    if (changes.has("disabled")) {
      this.buttons.forEach((radio) => radio.toggleAttribute("disabled", this.disabled));
      this.validation.validate(this.value ? this.value : void 0);
    }
    if (changes.has("required")) {
      this.validation.validate(this.value ? this.value : void 0);
    }
    this.id || (this.id = `leaf-radiogroup-${createUniqueId++}`);
    this.setFormControlElement(this);
  }
  render() {
    return html`
      <div class="leaf-c-radiogroup__wrapper">
        <slot @slotchange=${this.onSlotchange}></slot>
        <slot name="note"></slot>
      </div>
      ${this.validation.render()}
    `;
  }
  onFocusout(event) {
    if (!event.relatedTarget || !this.buttons.includes(event.relatedTarget)) {
      this.validation.validate(this.value);
    }
  }
  onSlotchange() {
    var _a;
    (_a = this.buttons) == null ? void 0 : _a.forEach((radio) => radio.checked = radio.value === this.value);
  }
  setValueForm() {
    const existingInput = this.internals.form.querySelector(`input[name="${this.name}"][id="${this.id}-hidden"]`);
    if (existingInput) {
      existingInput.remove();
    }
    if (this.name) {
      const newInput = document.createElement("input");
      newInput.name = this.name;
      newInput.id = `${this.id}-hidden`;
      newInput.hidden = true;
      newInput.value = this.value ? this.value : "";
      this.internals.form.appendChild(newInput);
    }
  }
};
_LeafRadiogroup.styles = unsafeCSS(styles);
_LeafRadiogroup.elementDefinitions = {
  "leaf-validation-message": LeafValidationMessage
};
_LeafRadiogroup.formAssociated = true;
let LeafRadiogroup = _LeafRadiogroup;
__decorateClass([
  property({ reflect: true })
], LeafRadiogroup.prototype, "variant", 2);
__decorateClass([
  property({ attribute: false })
], LeafRadiogroup.prototype, "validators", 2);
__decorateClass([
  property()
], LeafRadiogroup.prototype, "name", 2);
__decorateClass([
  property({ reflect: true })
], LeafRadiogroup.prototype, "value", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafRadiogroup.prototype, "disabled", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafRadiogroup.prototype, "invalid", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafRadiogroup.prototype, "required", 2);
export {
  LeafRadiogroup
};
//# sourceMappingURL=radiogroup.component.js.map
